import { z } from "zod";

export const PaymentMethodDataSchema = z.object({
  item_id: z.string().optional().nullable(),
  link_session_id: z.string().optional().nullable(),
  transfer_status: z.string().nullable().optional(),
  access_token: z.string().optional().nullable(),
  public_token: z.string().optional().nullable(),
  account_id: z.string().optional().nullable(),
  name_on_account: z.string().optional().nullable(),
  processor_token: z.string().optional().nullable(),
  counter_party_id: z.string().optional().nullable(),
  request_id: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  item: z.object({}).nullable().optional(),
  ach: z
    .object({
      routing_number: z.string().length(4),
      account_number: z.string().length(4),
      account_type: z.string(),
    })
    .optional()
    .nullable(),
  institution: z
    .object({
      name: z.string().optional().nullable(),
      institution_id: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  account: z
    .object({
      official_name: z.string().nullable().optional(),
      subtype: z.enum(["checking", "savings"]).optional().nullable(),
      name: z.string().optional().nullable(),
      id: z.string().optional().nullable(),
      type: z.string().optional().nullable(),
      verification_status: z.string().optional().nullable(),
      mask: z.string().length(4).optional().nullable(),
      holder_category: z.string().optional().nullable(),
      verification_name: z.string().nullable().optional(), // get the enum values
    })
    .optional()
    .nullable(),
});

export const PaymentMethodSchema = z.object({
  user_id: z.string(),
  plaid_item_id: z.string().optional().nullable(),
  plaid_auth_method: z.string().nullable(), // should be an enum
  payment_method_id: z.string(),
  payment_method_account_id: z.string().nullable().optional(),
  payment_method_status: z.enum([
    "active",
    "pending",
    "pending_manual_verification",
    "pending_automatic_verification",
    "database_insights_pass",
    "database_insights_pass_with_caution",
    "database_insights_pending",
    "automatically_verified",
    "manually_verified",
    "pending_disconnect",
    "expired",
  ]),
  payment_method_type: z.string(),
  payment_method_name: z.string(),
  payment_method_nickname: z.string().nullable(),
  payment_method_verified: z.boolean().optional(),
  payment_method_default: z.boolean(),
  payment_method_expiration_time: z
    .string()
    .datetime({ offset: true })
    .nullable(),
  payment_method_data: PaymentMethodDataSchema,
  account_first_name: z.string().min(1).nullable(),
  account_last_name: z.string().min(1).nullable(),
  account_business_name: z.string().min(1).nullable(),
  account_type: z
    .enum([
      "Personal Checking",
      "Personal Savings",
      "Business Checking",
      "Business Savings",
      "Credit Card",
    ])
    .nullable(),
  institution_info: z
    .object({
      institution_id: z.string().nullable(),
      institution_name: z.string().nullable(),
    })
    .nullable(),
});

export const UpdatePaymentMethodPayloadSchema = z.object({
  payment_method_id: z.string(),
  user_id: z.string().optional(),
  plaid_item_id: z.string().optional(),
  payment_method_status: z
    .enum([
      "active",
      "pending",
      "pending_manual_verification",
      "pending_automatic_verification",
      "database_insights_pass",
      "database_insights_pass_with_caution",
      "database_insights_pending",
      "automatically_verified",
      "manually_verified",
      "pending_disconnect",
    ])
    .optional(), //Needs the unum values
  payment_method_type: z.string().optional(),
  payment_method_name: z.string().optional(),
  payment_method_nickname: z.string().optional(),
  payment_method_default: z.boolean().optional(),
  payment_method_expiration_time: z
    .string()
    .datetime({ offset: true })
    .optional(),
  payment_method_data: z.object({}).optional(),
  account_first_name: z.string().min(1).optional(),
  account_last_name: z.string().min(1).optional(),
  account_business_name: z.string().min(1).optional(),
  account_type: z
    .enum([
      "Personal Checking",
      "Personal Savings",
      "Business Checking",
      "Business Savings",
      "Credit Card",
    ])
    .optional(),
});

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type UpdatePaymentMethodPayload = z.infer<
  typeof UpdatePaymentMethodPayloadSchema
>;
